import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Flex,
  FormControl,
  FormLabel,
  Button,
  IconButton,
  Text,
  Icon,
  Box,
  Spinner,
  RadioGroup,
  Radio,
  Stack,
  Checkbox,
  SimpleGrid,
  Divider,
} from '@chakra-ui/react';
import {
  FiLogIn,
  FiLogOut,
  FiInfo,
  FiCheckCircle,
  FiMapPin,
  FiArrowLeft,
  FiStar,
} from 'react-icons/fi';
import { listScanners } from 'services/qrService';
import { saveScannerId, clearScannerId } from '../scannerStorage';

const MODE_CONFIG = {
  access_log:   { label: 'Registro de Entrada', icon: FiLogIn,       bg: 'mint.bg', ink: 'mint.ink' },
  exit_log:     { label: 'Registro de Salida',  icon: FiLogOut,      bg: 'butter.bg', ink: 'butter.ink' },
  info_only:    { label: 'Solo Información',    icon: FiInfo,        bg: 'sky.bg', ink: 'sky.ink' },
  event_ticket: { label: 'Validación Entrada',  icon: FiCheckCircle, bg: 'lav.bg', ink: 'lav.ink' },
};

// Categorías que se ofrecen en la pantalla inicial de selección. La
// Validación Entrada (event_ticket) queda fuera a propósito — no forma
// parte de este flujo por decisión funcional.
const CATEGORIES = [
  { mode: 'access_log', title: 'Entrada',  subtitle: 'Registrar ingreso' },
  { mode: 'exit_log',   title: 'Salida',   subtitle: 'Registrar salida' },
  { mode: 'info_only',  title: 'Consulta', subtitle: 'Solo información' },
];

const modeOf = (mode) => MODE_CONFIG[mode] ?? MODE_CONFIG.access_log;

// forcedCategory (opcional): salta directo a la lista de lectores de esa
// categoría (Entrada/Salida/Consulta), sin pasar por la grilla "¿Qué vas a
// registrar?" — usado cuando quien llama (ScannerShell, ver el switch
// Entrada/Salida y el botón redondo de Consulta) ya sabe qué categoría
// quiere y solo necesita que el operador desempate entre 2+ lectores
// activos de ese mismo tipo. En este modo se oculta "Recordar este lector"
// — esa casilla persiste el lector por defecto de TODA la pantalla
// (loadSavedScannerId/saveScannerId, por inputMode), y una resolución
// puntual de ambigüedad (sobre todo para Consulta, que es una consulta
// transitoria) no debería pisar esa preferencia.
const ScannerConfig = ({ isOpen, onClose, currentScannerId, onSelect, inputMode, initialRemember, forcedCategory }) => {
  const [scanners, setScanners]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [view, setView]           = useState('category'); // 'category' | 'list'
  const [activeCategory, setActiveCategory] = useState(null);
  const [selected, setSelected]   = useState(currentScannerId ?? '');
  const [remember, setRemember]   = useState(initialRemember ?? false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    setView(forcedCategory ? 'list' : 'category');
    setActiveCategory(forcedCategory ?? null);
    setSelected(currentScannerId ?? '');
    setRemember(initialRemember ?? false);
    listScanners()
      .then(res => setScanners((res.data ?? []).filter(s => s.active)))
      .catch(() => setError('No se pudieron cargar los lectores.'))
      .finally(() => setLoading(false));
  }, [isOpen]); // eslint-disable-line

  const resolveScanner = (scanner) => {
    if (!scanner) return;
    // Con forcedCategory no hay casilla "Recordar" (ver arriba) — no toca
    // localStorage para nada, así una resolución puntual de ambigüedad
    // nunca pisa el lector recordado en este dispositivo.
    if (!forcedCategory) {
      if (remember) saveScannerId(inputMode, scanner.scanner_id);
      else clearScannerId(inputMode);
    }
    onSelect(scanner, remember);
    onClose();
  };

  const currentScanner = currentScannerId ? scanners.find(s => s.scanner_id === currentScannerId) : null;

  const bucketFor = (mode) => scanners.filter(s => s.mode === mode);

  const openCategory = (mode) => {
    const bucket = bucketFor(mode);
    if (bucket.length === 0) return;
    if (bucket.length === 1) {
      resolveScanner(bucket[0]);
      return;
    }
    setActiveCategory(mode);
    setSelected(bucket.some(s => s.scanner_id === currentScannerId) ? currentScannerId : '');
    setView('list');
  };

  const rememberCheckbox = (
    <Checkbox
      isChecked={remember}
      onChange={(e) => setRemember(e.target.checked)}
      sx={{ '& .chakra-checkbox__control[data-checked]': { background: '#EF5A2A', borderColor: '#EF5A2A' } }}
      fontSize="14.5px" fontWeight="600" color="ink.700"
    >
      Recordar este lector en este dispositivo
    </Checkbox>
  );

  const renderRow = (s) => {
    const m = modeOf(s.mode);
    const isActive = selected === s.scanner_id;
    return (
      <Box
        key={s.scanner_id}
        as="label"
        htmlFor={s.scanner_id}
        cursor="pointer"
        p={4}
        minH="60px"
        borderRadius="12px"
        border="2px solid"
        borderColor={isActive ? 'coral.500' : 'cream.line'}
        bg={isActive ? 'coral.tint' : 'white'}
        transition="all 0.15s"
        _hover={{ borderColor: 'coral.300' }}
      >
        <HStack justify="space-between" align="flex-start">
          <HStack spacing={3} align="flex-start">
            <Radio id={s.scanner_id} value={s.scanner_id} mt="2px" size="lg"
              sx={{ '&[data-checked]': { background: '#EF5A2A', borderColor: '#EF5A2A' } }} />
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="700" fontSize="15.5px" color="ink.900">
                {s.name}
              </Text>
              {s.location && (
                <HStack spacing={1} mt="2px">
                  <Icon as={FiMapPin} boxSize={3.5} color="ink.400" />
                  <Text fontSize="13px" color="ink.400" fontWeight="500">{s.location}</Text>
                </HStack>
              )}
            </VStack>
          </HStack>
          <Box
            as="span" display="inline-flex" alignItems="center" gap="5px"
            px="10px" py="4px" borderRadius="999px" bg={m.bg} color={m.ink}
            fontSize="12.5px" fontWeight="700" flexShrink={0}
          >
            <Icon as={m.icon} boxSize={3.5} />
            <Text>{m.label}</Text>
          </Box>
        </HStack>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
        <ModalHeader fontSize="18px" fontWeight="800" color="ink.900">
          {view === 'list'
            ? `Lectores de ${CATEGORIES.find(c => c.mode === activeCategory)?.title ?? ''}`
            : 'Seleccionar Lector'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {!forcedCategory && (
              <>
                <Box>{rememberCheckbox}</Box>
                <Divider />
              </>
            )}

            {loading && (
              <VStack py={8} spacing={3}>
                <Spinner color="coral.500" />
                <Text fontSize="14.5px" color="ink.400">Cargando lectores…</Text>
              </VStack>
            )}

            {error && (
              <Box bg="rose.bg" borderRadius="12px" p={3}>
                <Text fontSize="14.5px" color="rose.ink" fontWeight="600">{error}</Text>
              </Box>
            )}

            {!loading && !error && scanners.length === 0 && (
              <Text fontSize="14.5px" color="ink.400" textAlign="center" py={6}>
                No hay lectores activos. Créalos en Configuración → Lectores QR.
              </Text>
            )}

            {!loading && !error && scanners.length > 0 && view === 'category' && (
              <>
                {currentScanner && (
                  <Box
                    p={4} borderRadius="12px" border="2px solid" borderColor="coral.300" bg="coral.tint"
                  >
                    <HStack justify="space-between" align="center" flexWrap="wrap" spacing={2}>
                      <HStack spacing={2} align="center">
                        <Icon as={FiStar} boxSize={5} color="coral.600" />
                        <VStack align="flex-start" spacing={0}>
                          <Text fontSize="12.5px" color="ink.400" fontWeight="600">Lector actual</Text>
                          <Text fontSize="15.5px" fontWeight="700" color="ink.900">
                            {currentScanner.name}{currentScanner.location ? ` · ${currentScanner.location}` : ''}
                          </Text>
                        </VStack>
                      </HStack>
                      <Button
                        size="md" h="48px" fontSize="14.5px" borderRadius="9px" bg="coral.500" color="#fff" fontWeight="700"
                        _hover={{ bg: 'coral.600' }}
                        onClick={() => resolveScanner(currentScanner)}
                      >
                        Mantener este lector
                      </Button>
                    </HStack>
                  </Box>
                )}

                <FormControl>
                  <FormLabel fontSize="14.5px" color="ink.400" fontWeight="600" mb={3}>
                    ¿Qué vas a registrar?
                  </FormLabel>
                  <SimpleGrid columns={1} spacing={2}>
                    {CATEGORIES.map(cat => {
                      const bucket = bucketFor(cat.mode);
                      const m = modeOf(cat.mode);
                      const isEmpty = bucket.length === 0;
                      return (
                        <Box
                          key={cat.mode}
                          as="button"
                          type="button"
                          onClick={() => openCategory(cat.mode)}
                          disabled={isEmpty}
                          textAlign="left"
                          p={4}
                          minH="64px"
                          borderRadius="12px"
                          border="2px solid"
                          borderColor="cream.line"
                          bg={isEmpty ? 'cream.bg2' : 'white'}
                          opacity={isEmpty ? 0.5 : 1}
                          cursor={isEmpty ? 'not-allowed' : 'pointer'}
                          transition="all 0.15s"
                          _hover={isEmpty ? {} : { borderColor: 'coral.300' }}
                        >
                          <HStack justify="space-between">
                            <HStack spacing={3}>
                              <Flex w="46px" h="46px" borderRadius="12px" bg={m.bg} align="center" justify="center">
                                <Icon as={m.icon} boxSize={5} color={m.ink} />
                              </Flex>
                              <VStack align="flex-start" spacing={0}>
                                <Text fontWeight="700" fontSize="15.5px" color="ink.900">{cat.title}</Text>
                                <Text fontSize="13px" color="ink.400" fontWeight="500">
                                  {isEmpty ? 'Sin lectores activos' : cat.subtitle}
                                </Text>
                              </VStack>
                            </HStack>
                            {bucket.length > 1 && (
                              <Box
                                as="span" px="9px" py="3px" borderRadius="999px" bg="cream.bg2"
                                fontSize="12.5px" fontWeight="700" color="ink.700"
                              >
                                {bucket.length}
                              </Box>
                            )}
                          </HStack>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                </FormControl>
              </>
            )}

            {!loading && !error && view === 'list' && (
              <VStack align="stretch" spacing={3}>
                {!forcedCategory && (
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Volver"
                    title="Volver a categorías"
                    h="48px" w="48px" minW="48px"
                    fontSize="18px"
                    variant="ghost"
                    alignSelf="flex-start"
                    onClick={() => { setView('category'); setActiveCategory(null); }}
                  />
                )}
                <RadioGroup value={selected} onChange={setSelected}>
                  <Stack spacing={2}>
                    {bucketFor(activeCategory).map(renderRow)}
                  </Stack>
                </RadioGroup>
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" size="md" h="48px" fontSize="14.5px" onClick={onClose}>Cancelar</Button>
          {view === 'list' && (
            <Button
              size="md" h="48px" fontSize="14.5px" borderRadius="9px" bg="coral.500" color="#fff" fontWeight="700"
              _hover={{ bg: 'coral.600' }}
              onClick={() => resolveScanner(scanners.find(s => s.scanner_id === selected))}
              isDisabled={!selected || loading}
            >
              Usar este lector
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScannerConfig;
